import PageHeader from 'components/PageHeader';
import { Loader } from '@entur/loader';
import { SetStateAction, Dispatch, Suspense, useState } from 'react';
import { Head } from 'components/Head';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import { useToast } from '@entur/alert';
import { useAuth } from 'hooks/context/useAuthContext';
import { useForm } from 'react-hook-form';
import { TransactionTypeDto } from 'types';
import ErrorMessageNew from 'components/ErrorMessageNew';
import ToolBar from 'components/ToolBar';
import { SecondaryButton, PrimaryButton } from '@entur/button';
import ButtonRow from 'components/ButtonRow';
import {
  getTransactionTypeQueryOptions,
  useSuspenseTransactionType,
} from 'features/transaction-types/api/getTransactionType';
import { useUpdateTransactionType } from 'features/transaction-types/api/updateTransactionType';
import { TransactionTypeForm } from 'features/transaction-types/components/TransactionTypesForm';
import * as permissions from 'permissions';
import { useQueryClient } from '@tanstack/react-query';

const TransactionTypeDetailsRoute = () => {
  const { id } = useParams();
  const intl = useIntl();
  const [readOnly, setReadOnly] = useState(true);

  return (
    <>
      <Head title={`${intl.formatMessage({ id: 'transactionTypes.name' })} #${id}`} />
      <PageHeader heading={intl.formatMessage({ id: 'transactionTypes.name' })} element={id} nextButton={readOnly} />

      {id && (
        <ErrorBoundary>
          <Suspense key={id} fallback={<Loader>{intl.formatMessage({ id: 'generalValues.loading' })}</Loader>}>
            <TransactionTypeDetails id={id} readOnly={readOnly} setReadOnly={setReadOnly} />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};

interface TransactionTypeDetailsProps {
  id: string;
  readOnly: boolean;
  setReadOnly: Dispatch<SetStateAction<boolean>>;
}

const TransactionTypeDetails = ({ id, readOnly, setReadOnly }: TransactionTypeDetailsProps) => {
  const intl = useIntl();
  const { addToast } = useToast();
  const { hasPrivilege } = useAuth();

  const canEdit = () => {
    return status !== 3 && hasPrivilege(permissions.getPermission(permissions.ADMIN, permissions.DELETE));
  };

  const {
    data: { data: transactionType },
  } = useSuspenseTransactionType({ id: id });

  const formContext = useForm<TransactionTypeDto>({
    mode: 'onBlur',
    defaultValues: transactionType,
  });

  const { handleSubmit, reset, watch } = formContext;
  const status = watch('status');
  const queryClient = useQueryClient();

  const {
    isPending: updateIsPending,
    mutate,
    error,
  } = useUpdateTransactionType({
    mutationConfig: {
      onSuccess: (response) => {
        addToast(intl.formatMessage({ id: 'toastAlerts.saved' }));
        reset(response.data);
        setReadOnly(true);
        queryClient.invalidateQueries({ queryKey: getTransactionTypeQueryOptions(id).queryKey });
      },
    },
  });

  const handleUpdate = (data: TransactionTypeDto) => {
    mutate({ id, data });
  };

  const handleCancel = () => {
    setReadOnly(true);
    reset();
  };

  return (
    <>
      <ErrorMessageNew error={error} />
      <ToolBar
        editButtonText={canEdit() ? intl.formatMessage({ id: 'transactionTypes.name' }).toLowerCase() : undefined}
        readonly={readOnly}
        setReadonly={setReadOnly}
      />
      <TransactionTypeForm formContext={formContext} mode={!readOnly ? 'edit' : 'view'} />
      <ButtonRow>
        <ButtonRow.RightGroup>
          {!readOnly && (
            <>
              <SecondaryButton onClick={handleCancel}>
                {intl.formatMessage({ id: 'generalValues.cancel' })}
              </SecondaryButton>
              <PrimaryButton onClick={handleSubmit(handleUpdate)} loading={updateIsPending} disabled={updateIsPending}>
                {intl.formatMessage({ id: 'generalValues.save' })}
              </PrimaryButton>
            </>
          )}
        </ButtonRow.RightGroup>
      </ButtonRow>
    </>
  );
};

export default TransactionTypeDetailsRoute;
